import React, { createContext, useContext, useMemo } from 'react';
import type {
  WorkflowFlowTriggerData,
  WorkflowNodeViewData,
} from './components';

/**
 * What the editor is currently drilled into. Node and trigger share one detail
 * view (only one is ever open). Adding a trigger is a separate popup modal, not
 * an artifact-panel drill, so it isn't a target here.
 */
export type DrillTarget =
  | { readonly kind: 'node'; readonly node: WorkflowNodeViewData }
  | { readonly kind: 'trigger'; readonly data: WorkflowFlowTriggerData }
  | null;

type WorkflowDrillValue = {
  readonly target: DrillTarget;
  /** Close the form and return focus to the workflow canvas artifact. */
  readonly close: () => void;
};

const WorkflowDrillContext = createContext<WorkflowDrillValue | null>(null);

/**
 * Drill context for hosting a node/trigger form inside the chat **artifact
 * panel** (TJ's "node = artifact" model). The `target` is fixed (it came from
 * the artifact payload) and `close` returns focus to the workflow canvas
 * artifact. The node/trigger edit drawers consume this.
 */
export const ArtifactDrillProvider: React.FC<{
  target: DrillTarget;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ target, onClose, children }) => {
  const value = useMemo<WorkflowDrillValue>(
    () => ({ target, close: onClose }),
    [target, onClose]
  );
  return (
    <WorkflowDrillContext.Provider value={value}>
      {children}
    </WorkflowDrillContext.Provider>
  );
};

export function useWorkflowDrill(): WorkflowDrillValue {
  const ctx = useContext(WorkflowDrillContext);
  if (ctx === null) {
    throw new Error(
      'useWorkflowDrill must be used within ArtifactDrillProvider'
    );
  }
  return ctx;
}
